/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import { useUIDSeed } from 'react-uid';
import { useTracking } from 'react-tracking';
import {
    shape, object, arrayOf, string, number, func, bool, array,
} from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useLocation } from 'react-router';

import noop from '../../../../helpers/noop';
import customBreakpoints from '../../../../helpers/customBreakpoints';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { GraphqlSimpleProduct } from '../../GraphqlCategoryPage/Partials/GraphqlProductContainer/Partials/GraphqlSimpleProduct/GraphqlSimpleProduct';
import { emitProductImpression } from '../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { getFeatureFlag, getFeatureFlags, getPresentationFamily } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import LoadMoreButton from '../../GraphqlCategoryPage/Partials/GraphqlProductContainer/Partials/LoadMoreButton';
import usePrevious from '../../../../gql/hooks/usePrevious';

const styles = (theme) => ({
    main: {
        display: 'flex',
        flexWrap: 'wrap',
        [theme.breakpoints.up(1024)]: {
            margin: '0 15%',
        },
    },
    fullWidthMain: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    simpleProduct3row: {
        flex: '0 0 33.33%',
        marginTop: '8px',
        marginBottom: '30px',
        maxWidth: '33.33%',
        position: 'relative',
        width: '100%',
        paddingRight: '10px',
        paddingLeft: '10px',
        [theme.breakpoints.down(598)]: {
            flex: '0 0 50%',
            maxWidth: '50%',
        },
    },
    simpleProduct4row: {
        flex: '0 0 25%',
        marginTop: '8px',
        marginBottom: '30px',
        maxWidth: '25%',
        position: 'relative',
        width: '100%',
        paddingRight: '10px',
        paddingLeft: '10px',
        [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
            flex: '0 0 50%',
            maxWidth: '50%',
        },
    },
    simpleProduct5row: {
        flex: '0 0 20%',
        maxWidth: '20%',
        position: 'relative',
        width: '100%',
        paddingRight: '10px',
        paddingLeft: '10px',
        [theme.breakpoints.down('xs')]: {
            flex: '0 0 50%',
            maxWidth: '50%',
        },
    },
    simpleProduct6row: {
        flex: '0 0 16.666666%',
        maxWidth: '16.666666%',
        position: 'relative',
        width: '100%',
        paddingRight: '10px',
        paddingLeft: '10px',
        '& $productImage': {
            minHeight: 'auto',
            '& img': {
                width: '100%',
            },
        },
        [theme.breakpoints.down('xs')]: {
            flex: '0 0 50%',
            maxWidth: '50%',
        },
    },
    fullWidthContainer: {
        width: '100%',
    },
});

const GraphqlSearchProducts = ({
    classes,
    fetchMoreResults,
    pageNumber,
    productNotAvaliable,
    products = [],
    productsPerRow,
    totalPages,
    searchTerm,
    featureFlags,
    totalProducts,
    track,
    presentationFamily,
    eventSubType,
    categoryId,
    device,
    emitProductImpressionEvent,
    variables,
    prevProductCount,
    priceRangeData,
    experiments,
    productsToLoadOnLoadMoreButton,
    fullWidth,
}) => {
    const seed = useUIDSeed();
    const tracking = useTracking();
    const [loading, setLoading] = useState(false); // Load More button loading
    const ffIsLoadMoreOptimizationEnabled = useSelector(getFeatureFlag('is-load-more-optimization-enabled'));
    const ffIsLoadMoreCountShown = useSelector(getFeatureFlag('is-load-more-count-shown'));
    const location = useLocation();

    useEffect(() => {
        track({
            nonInteraction: '1',
            eventCategory: 'Test Impression',
            eventAction: '<<pageType>>',
            eventLabel: 'Load More - Variant',
        });
    }, []);
    // loadMore button loading
    useEffect(() => {
        setLoading(false);
    }, [products.length]);

    const productIds = products.map((product) => product?.id || product?.partNumber);
    const prevProductIds = usePrevious(productIds);
    const variablesKey = JSON.stringify(variables || {});
    const previousVariablesKey = usePrevious(variablesKey);
    const searchContextKey = JSON.stringify({
        pathname: location?.pathname,
        search: location?.search,
        searchTerm,
        eventSubType,
    });
    const previousSearchContextKey = usePrevious(searchContextKey);

    const isLoadMoreAvailable = totalProducts > products?.length;
    const productsToLoad = ffIsLoadMoreOptimizationEnabled ? 36 : 12; // number of product when we click on load more button
    const isSameProductList = productIds.length === (prevProductIds?.length || 0)
        && productIds.every((id, index) => id === prevProductIds?.[index]);
    const isSameContext = previousVariablesKey === variablesKey
        && previousSearchContextKey === searchContextKey;
    const impressionPrevProductCount = pageNumber > 1 ? prevProductCount : 0;
    const trackedProducts = pageNumber > 1 ? products.slice(prevProductCount) : products;

    useEffect(() => {
        if (!trackedProducts?.length) {
            return;
        }

        // Search load-more updates page state before Apollo appends the new products.
        // Only track when the visible product list or search/filter context has actually changed,
        // otherwise we can emit a duplicate listing event with stale page-1 data.
        if (isSameProductList && isSameContext) {
            return;
        }

        // For pagination, send only the newly appended slice so listing data does not restart
        // from page 1 on every load-more interaction.
        tracking.trackEvent({
            products: trackedProducts,
            action: 'track_product_listing_data',
        });
    }, [isSameContext, isSameProductList, trackedProducts, tracking]);

    useEffect(() => {
        if (!products?.length) {
            return;
        }

        // Keep the product-impression event aligned with the rendered list.
        // If neither the products nor the search/filter context changed, skip this rerender.
        if (isSameProductList && isSameContext) {
            return;
        }

        // Reset previous count for a fresh search/filter result set, but preserve the count for
        // true load-more appends so downstream clickstream positions start after the prior page.
        emitProductImpressionEvent({
            productsPayload: {
                products,
                prevProductLength: impressionPrevProductCount,
                productPageSize: productsToLoadOnLoadMoreButton || productsToLoad,
                productsPerRow,
                mobileBreakpoint: 1024,
                variables,
                priceRangeData,
            },
            page: { type: eventSubType, searchTerm },
            experiments,
            category: { id: categoryId },
        });
    }, [
        emitProductImpressionEvent,
        eventSubType,
        experiments,
        impressionPrevProductCount,
        isSameContext,
        isSameProductList,
        pageNumber,
        priceRangeData,
        products,
        productsPerRow,
        productsToLoad,
        productsToLoadOnLoadMoreButton,
        searchTerm,
        variables,
    ]);

    const categoryRow = () => {
        let cssPerRow = classes.simpleProduct3row;
        if (productsPerRow === 4 || productNotAvaliable) {
            cssPerRow = classes.simpleProduct4row;
        }
        if (productsPerRow === 5) {
            cssPerRow = classes.simpleProduct5row;
        }
        if (productsPerRow === 6) {
            cssPerRow = classes.simpleProduct6row;
        }
        return cssPerRow;
    };
    if (featureFlags['is-passport-eligible-banner']) {
        track({
            eventCategory: 'Test Impression', eventAction: 'passport eligible', eventLabel: 'search', nonInteraction: '1',
        });
    }

    // showing the product list conatiner on searchPage
    const ProductContainer = (
        <div className={fullWidth ? classes.fullWidthMain : classes.main}>
            {products?.map((product, i) => {
                const isFoodOrNotPersonalizable = (presentationFamily === 'food' || (!product?.isPersonalizable && product?.brand !== 'personalizationmall' && product?.brand !== 'thingsremembered'));
                const displayQuickview = featureFlags['is-search-quickview-modal-enabled'] && product?.productType !== 'CUSTOM_ASSORTMENT' && isFoodOrNotPersonalizable;
                return (
                    <Grid
                        className={categoryRow()}
                        key={seed(product)}
                        data-testid="search-components"
                    >
                        <GraphqlSimpleProduct
                            position={i + 1}
                            searchTerm={searchTerm}
                            quickview={displayQuickview}
                            name={product?.name}
                            image={product?.image}
                            url={product?.seo?.url}
                            skuPriceRange={product?.skuPriceRange}
                            availability={product?.availability}
                            partNumber={product?.partNumber}
                            productNotAvaliable={product?.productNotAvaliable}
                            showSimpleProductRedesignAbTest={false}
                            productBrand={product.brandId}
                            productReviews={product?.reviews}
                            isCategoryPage
                            productSkus={product?.productSkus}
                            isPassportEligible={product?.isPassportEligible}
                            experiments={experiments?.filter((exp) => exp.source !== 'vertex-ai')}
                            track
                            trackData={{
                                tracking_event_category: 'Search Page',
                                tracking_event_action: 'Product Click',
                            }}
                        />
                    </Grid>
                );
            })}
        </div>
    );

    return (
        <>
            {ProductContainer}
            <LoadMoreButton
                hasMore={pageNumber <= totalPages}
                loading={loading}
                isLoadMoreAvailable={isLoadMoreAvailable}
                ssrDeviceType={device}
                path={location?.pathname}
                setLoading={setLoading}
                onLoadMore={fetchMoreResults}
                productToLoad={productsToLoadOnLoadMoreButton || productsToLoad}
                totalProducts={products?.length}
                totalProductsToLoad={totalProducts}
                ffIsLoadMoreOptimizationEnabled={ffIsLoadMoreOptimizationEnabled}
                ffIsLoadMoreCountShown={ffIsLoadMoreCountShown}

                tracking={{
                    eventCategory: 'Search Page',
                    eventAction: 'Load More',
                    eventLabel: location?.pathname || '<<pageType>>',
                }}
            />
        </>

    );
};

GraphqlSearchProducts.propTypes = {
    classes: object.isRequired,
    presentationFamily: string.isRequired,
    products: arrayOf(shape({
        availability: shape({
            availabilityIndicator: string,
            deliveryDateType: string,
            deliveryMessage: string,
            displayEndDate: string,
            displayStartDate: string,
            earliestShipDate: string,
            latestShipDate: string,
            perishable: string,
            productDeliveryType: string,
            shipAlone: string,
            __typename: string,
        }),
        brand: string,
        image: shape({
            altText: string,
            name: string,
            snipe: string,
            __typename: string,
        }),
        name: string,
        partNumber: string,
        prices: arrayOf(shape({
            currency: string,
            type: string,
            value: number,
            __typename: string,
        })),
        seo: shape({
            url: string,
            __typename: string,
        }),
    })).isRequired,
    pageNumber: number,
    totalPages: number,
    productNotAvaliable: bool,
    fetchMoreResults: func,
    productsPerRow: number,
    searchTerm: string.isRequired,
    featureFlags: shape({
        'is-search-quickview-modal-enabled': bool,
    }),
    track: func,
    eventSubType: string,
    totalProducts: number.isRequired,
    device: string.isRequired,
    emitProductImpressionEvent: func,
    variables: object,
    prevProductCount: number,
    priceRangeData: object,
    experiments: array,
    productsToLoadOnLoadMoreButton: number,
    fullWidth: bool,
    categoryId: string,
};

GraphqlSearchProducts.defaultProps = {
    pageNumber: 1,
    productNotAvaliable: false,
    totalPages: '',
    eventSubType: '',
    fetchMoreResults: () => { },
    productsPerRow: 4,
    featureFlags: {
        'is-search-quickview-modal-enabled': false,
    },
    track: () => { },
    emitProductImpressionEvent: noop,
    variables: {},
    prevProductCount: 0,
    priceRangeData: {},
    experiments: [],
    productsToLoadOnLoadMoreButton: null,
    fullWidth: false,
    categoryId: '',
};

const mapStatesToProps = (state) => ({
    featureFlags: getFeatureFlags(state),
    presentationFamily: getPresentationFamily(state),
});

const mapDispatchToProps = (dispatch) => ({
    track: bindActionCreators(trackEvent, dispatch),
    emitProductImpressionEvent: bindActionCreators(emitProductImpression, dispatch),
});

const enhance = compose(
    withStyles(styles),
    connect(mapStatesToProps, mapDispatchToProps),
);

export default enhance(GraphqlSearchProducts);
